import SectorActions from 'actions/Sector'
import VariableActions from 'actions/Variable'
import ActionStatus from 'components/ActionStatus'
import {
    Role,
    listOfRoles
} from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { useEffect, useMemo, useState } from 'react'
import {
    Control,
    FieldErrors,
    FieldValues,
    UseFormRegister,
    UseFormResetField,
    UseFormSetValue,
    UseFormWatch
} from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import { IAuthenticationReducer } from 'types/autentication'
import { NotificationAction, NotificationType } from 'types/notification'
import { SectorAction } from 'types/sector'
import { VariableAction, VariableReducer } from 'types/variable'
import { InfoInputsWrapper } from './styles'
import { InfoWithoutInputs } from './Children/InfoWithoutInputs'
import { TopInfos } from './Children/TopInfos'
import { FormInputs } from './Children/FormInputs'
import { NotificationGuidance } from './Children/NotificationGuidance'
import { RootReducerState } from 'types/global'
import NotificationActions from 'actions/Notification'
import { useParams } from 'react-router-dom'
import { notificationStatus } from 'constants/notificationStatus'
import { isSevereOrDeathDamage } from 'utils/damage'
interface NotificationDataProps {
    auth: IAuthenticationReducer
    register: UseFormRegister<FieldValues>
    setValue: UseFormSetValue<FieldValues>
    control: Control<FieldValues, any>
    statusIsPendingFinishOrFinished: boolean
    watch: UseFormWatch<FieldValues>
    tooltipAlert: string
    statusEdit: boolean
    notification: NotificationType
    errors: FieldErrors<FieldValues>
    dirtyFields: Partial<FieldValues>
    resetField: UseFormResetField<FieldValues>
    setFileUploadS3Attached?: React.Dispatch<React.SetStateAction<File>>
    setUrlUploadS3AttachedFile?: React.Dispatch<React.SetStateAction<string>>
    setUrlDeleteS3AttachedFile?: React.Dispatch<React.SetStateAction<string>>
    loggedInUserIsResponsible?: boolean
    notificationStatusState?: string
}

export const enabledStepOneRoles = [
    listOfRoles.admin,
    listOfRoles.location_qa,
    listOfRoles.corporate_quality_board
]
export const canEditStepOneRoles = (loggedUserRole: Role) => {
    return enabledStepOneRoles.includes(loggedUserRole)
}
const NotificationData: React.FC<NotificationDataProps> = ({
    auth,
    register,
    setValue,
    control,
    dirtyFields,
    statusEdit,
    notification,
    watch,
    tooltipAlert,
    statusIsPendingFinishOrFinished,
    errors,
    resetField,
    setFileUploadS3Attached,
    setUrlUploadS3AttachedFile,
    setUrlDeleteS3AttachedFile,
    loggedInUserIsResponsible = false,
    notificationStatusState = '',
}) => {
    
    const variableDispatch: Dispatch<VariableAction> = useDispatch()
    const sectorDispatch: Dispatch<SectorAction> = useDispatch()
    const notificationDispatch: Dispatch<NotificationAction>  = useDispatch()
    const { id } = useParams()
    
    const variableSelector: VariableReducer = useSelector((state: RootReducerState) => state.variableReducers)
    
    useEffect(() => {
        NotificationActions.getNotificationById(notificationDispatch, id)
        VariableActions.getAllVariables(variableDispatch)
        SectorActions.getAllSectors(sectorDispatch)
        NotificationActions.getSentinelEvent(notificationDispatch);
        NotificationActions.getNeverEvent(notificationDispatch);
    }, [auth?.dataJwt])

    const canEditStepOne = useMemo(() => canEditStepOneRoles(auth?.dataJwt?.profile), [auth?.dataJwt])
    
    // Verifica se o usuário tem acesso à mesma localização e setor da notificação
    const isSameLocation = useMemo(() => {
        return auth?.dataJwt?.locations?.filter(
            (i) => i.sys_id === notification?.location_incident?.value
        ).length > 0
    }, [auth?.dataJwt?.locations, notification?.location_incident?.value])
    
    const isSameSector = useMemo(() => {
        if (!isSameLocation) return false
        return auth?.dataJwt?.locations
            ?.filter((i) => i.sys_id === notification?.location_incident?.value)[0]
            ?.sectors.filter((i) => i.sys_id === notification?.incident_sector?.value).length > 0
    }, [isSameLocation, auth?.dataJwt?.locations, notification?.location_incident?.value, notification?.incident_sector?.value])
    
    // Verifica se pode editar considerando perfil E se é responsável
    // Nos status "Nova" e "Aguardando", não exige que o usuário seja responsável
    // pois ainda não há usuário atribuído à notificação
    const canActuallyEdit = useMemo(() => {
        // Se não há status definido ainda, permite edição para perfis habilitados
        // (isso pode acontecer durante o carregamento inicial)
        if (!notificationStatusState) {
            return canEditStepOne || (auth?.dataJwt?.profile === listOfRoles.admin)
        }
        
        const isNewOrWaitingStatus = notificationStatusState === notificationStatus[0].label || 
                                     notificationStatusState === notificationStatus[1].label
        
        // Se for status "Nova" ou "Aguardando", verifica perfil incluindo location_manager
        if (isNewOrWaitingStatus) {
            // Admin sempre pode editar
            if (auth?.dataJwt?.profile === listOfRoles.admin) {
                return true
            }
            // Location_qa e corporate_quality_board podem editar
            if (canEditStepOne) {
                return true
            }
            // Location_manager pode editar se for mesma localização e setor
            if (auth?.dataJwt?.profile === listOfRoles.location_manager && isSameLocation && isSameSector) {
                return true
            }
            return false
        }
        
        // Para outros status, exige que seja responsável E tenha perfil adequado
        if (!canEditStepOne) {
            return false
        }
        return loggedInUserIsResponsible
    }, [canEditStepOne, loggedInUserIsResponsible, notificationStatusState, auth?.dataJwt?.profile, isSameLocation, isSameSector, notification])

    // Verifica se admin ou corporate_quality_board podem editar apenas o campo de orientação
    // (mesmo que não sejam responsáveis pela notificação)
    const canEditGuidanceField = useMemo(() => {
        const profile = auth?.dataJwt?.profile
        return profile === listOfRoles.admin || profile === listOfRoles.corporate_quality_board
    }, [auth?.dataJwt?.profile])

    // Calcula se deve mostrar o NotificationGuidance (mesma lógica do FormInputs)
    const imageRisk = watch('u_image_risk')
    const damageLevel = watch('damage_level')
    const isProfileCorporateQualityBoard = auth?.dataJwt?.profile === listOfRoles.corporate_quality_board
    const obitoOrGraveSelected = useMemo(() => {
        return isSevereOrDeathDamage(damageLevel)
    }, [damageLevel])
    const isProfileCorporateQualityBoardWithImageRisk = useMemo(() => {
        return notification?.u_image_risk?.value?.includes('yes') && isProfileCorporateQualityBoard
    }, [notification?.u_image_risk?.value, isProfileCorporateQualityBoard])
    const isShowNotificationGuidance = useMemo(() => {
        return imageRisk?.includes('yes') || isProfileCorporateQualityBoardWithImageRisk || obitoOrGraveSelected
    }, [imageRisk, isProfileCorporateQualityBoardWithImageRisk, obitoOrGraveSelected])

    return (
        <div data-testid="test-notification-treatment-step1">
            <InfoInputsWrapper>
                <TopInfos
                    auth={auth}
                    notification={notification}
                    setFileUploadS3Attached={setFileUploadS3Attached}
                    setUrlUploadS3AttachedFile={setUrlUploadS3AttachedFile}
                    setUrlDeleteS3AttachedFile={setUrlDeleteS3AttachedFile}
                    control={control}
                    dirtyFields={dirtyFields}
                />

                {statusEdit && (
                    <div>
                        <ActionStatus
                            text="Notificação editada com sucesso!"
                            status={1}
                        />
                    </div>
                )}
                {tooltipAlert && (
                    <div data-testid="test-tooltip-alert">
                        <ActionStatus text={tooltipAlert} status={1} />
                    </div>
                )}

                {canEditStepOne && notification ? (
                    <>
                        {/* Parte superior com largura condicional */}
                        <div className="grid lg:grid-cols-3 grid-cols-2 overflow-visible items-start">
                            <fieldset 
                                disabled={statusIsPendingFinishOrFinished || !canActuallyEdit} 
                                className={isShowNotificationGuidance ? 'col-span-2' : 'col-span-3'}
                            >
                                <FormInputs
                                    variableSelector={variableSelector}
                                    resetField={resetField}
                                    dirtyFields={dirtyFields}
                                    watch={watch}
                                    setValue={setValue}
                                    register={register}
                                    control={control}
                                    auth={auth}
                                    canEditStepOne={canEditStepOne}
                                    errors={errors}
                                    notification={notification}
                                    statusIsPendingFinishOrFinished={
                                        statusIsPendingFinishOrFinished
                                    }
                                    loggedInUserIsResponsible={loggedInUserIsResponsible}
                                    canEditGuidanceField={canEditGuidanceField}
                                    showTopSectionOnly={true}
                                />
                            </fieldset>
                            
                            {/* NotificationGuidance renderizado FORA do fieldset disabled para permitir edição */}
                            {isShowNotificationGuidance && (
                                <NotificationGuidance
                                    variableSelector={variableSelector}
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    showNotificationGuidance={() => true}
                                    authProfile={auth?.dataJwt?.profile}
                                    register={register}
                                    notification={notification}
                                    forceEnabled={canEditGuidanceField}
                                />
                            )}
                        </div>
                        
                        {/* Parte inferior sempre com largura máxima */}
                        <fieldset 
                            disabled={statusIsPendingFinishOrFinished || !canActuallyEdit}
                        >
                            <FormInputs
                                variableSelector={variableSelector}
                                resetField={resetField}
                                dirtyFields={dirtyFields}
                                watch={watch}
                                setValue={setValue}
                                register={register}
                                control={control}
                                auth={auth}
                                canEditStepOne={canEditStepOne}
                                errors={errors}
                                notification={notification}
                                statusIsPendingFinishOrFinished={
                                    statusIsPendingFinishOrFinished
                                }
                                loggedInUserIsResponsible={loggedInUserIsResponsible}
                                canEditGuidanceField={canEditGuidanceField}
                                showTopSectionOnly={false}
                            />
                        </fieldset>
                    </>
                ) : (
                    <fieldset disabled={statusIsPendingFinishOrFinished || !canActuallyEdit}>
                        <InfoWithoutInputs
                            variableSelector={variableSelector}
                            control={control}
                            notification={notification}
                            auth={auth}
                            watch={watch}
                        />
                    </fieldset>
                )}
            </InfoInputsWrapper>
        </div>
    )
}

export { NotificationData }
